import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DiscogsImportCard } from "@/components/settings/DiscogsImportCard";
import { Logo } from "@/components/Logo";

export const Route = createFileRoute("/_authenticated/onboarding")({
  head: () => ({
    meta: [
      { title: "Bem-vindo ao Viny — Comece sua coleção" },
      { name: "description", content: "Conte seus gostos musicais e importe sua coleção do Discogs." },
      { property: "og:title", content: "Bem-vindo ao Viny" },
      { property: "og:description", content: "Conte seus gostos musicais e importe sua coleção do Discogs." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Onboarding,
});

const FALLBACK = ["MPB", "Rock", "Jazz", "Soul", "Samba", "Bossa Nova", "Hip Hop", "Funk", "Pop", "Eletrônica", "Indie", "Blues", "Reggae", "Clássica"];

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<0 | 1>(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [custom, setCustom] = useState("");
  const [extra, setExtra] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const popular = useQuery({
    queryKey: ["onboarding-popular-genres"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("favorite_genres").not("favorite_genres", "is", null).limit(1000);
      const count = new Map<string, { label: string; n: number }>();
      for (const r of data ?? []) {
        for (const g of (r.favorite_genres as string[] | null) ?? []) {
          const label = g.trim();
          if (!label || label.length > 30) continue;
          const k = label.toLowerCase();
          const cur = count.get(k);
          if (cur) cur.n++;
          else count.set(k, { label, n: 1 });
        }
      }
      const list = [...count.values()].sort((a, b) => b.n - a.n).map((x) => x.label).slice(0, 24);
      const seen = new Set(list.map((l) => l.toLowerCase()));
      for (const f of FALLBACK) if (list.length < 18 && !seen.has(f.toLowerCase())) list.push(f);
      return list;
    },
  });

  const chips = [...(popular.data ?? FALLBACK), ...extra];
  const toggle = (g: string) =>
    setSelected((s) => (s.includes(g) ? s.filter((x) => x !== g) : [...s, g]));

  function addCustom() {
    const g = custom.trim();
    if (!g) return;
    if (!chips.some((c) => c.toLowerCase() === g.toLowerCase())) setExtra((e) => [...e, g]);
    if (!selected.includes(g)) setSelected((s) => [...s, g]);
    setCustom("");
  }

  async function saveTastes() {
    if (selected.length === 0) return setStep(1);
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("profiles").update({ favorite_genres: selected.slice(0, 20) }).eq("id", u.user!.id);
    setSaving(false);
    if (error) toast.error("Não foi possível salvar seus gostos");
    setStep(1);
  }

  const finish = () => navigate({ to: "/explore" });

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background text-foreground">
      <header className="flex items-center justify-between px-6 py-5">
        <Logo variant="light" size={20} withLink={false} />
        <div className="flex gap-1.5">
          {[0, 1].map((i) => (
            <span key={i} className={`h-1.5 w-8 rounded-full ${i <= step ? "bg-action" : "bg-muted"}`} />
          ))}
        </div>
        <button onClick={step === 0 ? () => setStep(1) : finish} className="text-sm text-muted-foreground hover:text-foreground">
          Pular
        </button>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-6 pb-12">
        {step === 0 ? (
          <>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Passo 1 de 2</p>
            <h1 className="mt-2 text-3xl font-semibold sm:text-4xl">O que toca na sua vitrola?</h1>
            <p className="mt-3 text-muted-foreground">Escolha seus gêneros favoritos. Eles aparecem no seu perfil e ajudam a te conectar com quem curte o mesmo som.</p>

            <div className="mt-8 flex flex-wrap gap-2">
              {chips.map((g) => {
                const on = selected.includes(g);
                return (
                  <button
                    key={g}
                    onClick={() => toggle(g)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition ${
                      on ? "border-action bg-action text-action-foreground" : "border-border bg-card hover:border-foreground/40"
                    }`}
                  >
                    {on && <Check className="h-3.5 w-3.5" />}
                    {g}
                  </button>
                );
              })}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                addCustom();
              }}
              className="mt-5 flex gap-2"
            >
              <input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                maxLength={30}
                placeholder="Adicionar outro gênero"
                className="h-11 flex-1 rounded-full border border-border bg-card px-4 text-sm outline-none focus:border-action"
              />
              <button type="submit" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card">
                <Plus className="h-4 w-4" />
              </button>
            </form>

            <button
              onClick={saveTastes}
              disabled={saving}
              className="mt-10 h-12 rounded-full bg-action px-8 font-medium text-action-foreground disabled:opacity-60 sm:self-start"
            >
              {saving ? "Salvando..." : selected.length ? `Continuar (${selected.length})` : "Continuar"}
            </button>
          </>
        ) : (
          <>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Passo 2 de 2</p>
            <h1 className="mt-2 text-3xl font-semibold sm:text-4xl">Já tem coleção no Discogs?</h1>
            <p className="mt-3 text-muted-foreground">Conecte sua conta e traga seus discos e sua lista de desejos em segundos. Os importados não aparecem no Explorar.</p>
            <div className="mt-8">
              <DiscogsImportCard />
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <button onClick={finish} className="h-12 rounded-full bg-action px-8 font-medium text-action-foreground">
                Concluir
              </button>
              <button onClick={finish} className="h-12 rounded-full px-6 text-sm text-muted-foreground hover:text-foreground">
                Pular por enquanto
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
